import React from 'react';
import {
  AutocompleteInput,
  Create,
  Edit,
  FunctionField,
  NumberField,
  NumberInput,
  ReferenceInput,
  required,
  Show,
  SimpleForm,
  SimpleShowLayout,
  TextField,
  TextInput,
} from 'react-admin';
import type {
  AdminExerciseBaseBasicsFragment,
  AdminExerciseTypeBasicsFragment,
  AdminExerciseTypeCategoryBasicsFragment,
  AdminExerciseTypeCategoryGroupBasicsFragment,
} from '../../../graphql/types';
import { ResourceLink } from '../../reusable/ResourceLink';
import ClipboardField from '../components/ClipboardField';
import { CategoryHierarchyEditToolbar } from './CategoryActions';
import type { ChildrenTableColumn } from './ChildrenTableSection';
import { ChildrenTableSection } from './ChildrenTableSection';

type ExerciseTypeCategoryRecord = AdminExerciseTypeCategoryBasicsFragment & {
  exerciseTypeCategoryGroupId?: string | null;
};
type ExerciseTypeRecord = AdminExerciseTypeBasicsFragment & {
  exerciseTypeCategoryId?: string | null;
};
type ExerciseBaseRecord = AdminExerciseBaseBasicsFragment & {
  exerciseTypeId?: string | null;
};

const exerciseReferenceInputProps = {
  perPage: 100,
  sort: { field: 'name', order: 'ASC' as const },
};

export const ExerciseTypeCategoryGroupCreate: React.FC = () => (
  <Create resource="ExerciseTypeCategoryGroup" redirect="show">
    <SimpleForm>
      <TextInput source="name" label="Name" validate={required()} fullWidth />
    </SimpleForm>
  </Create>
);

export const ExerciseTypeCategoryCreate: React.FC = () => (
  <Create resource="ExerciseTypeCategory" redirect="show">
    <SimpleForm>
      <ReferenceInput
        source="exerciseTypeCategoryGroupId"
        reference="ExerciseTypeCategoryGroup"
        {...exerciseReferenceInputProps}
      >
        <AutocompleteInput
          fullWidth
          optionText="name"
          optionValue="id"
          label="MTC Group"
          filterToQuery={(name: string) => ({ name })}
          validate={required()}
        />
      </ReferenceInput>
      <TextInput source="name" label="Name" validate={required()} fullWidth />
    </SimpleForm>
  </Create>
);

export const ExerciseTypeCreate: React.FC = () => (
  <Create resource="ExerciseType" redirect="show">
    <SimpleForm>
      <ReferenceInput
        source="exerciseTypeCategoryId"
        reference="ExerciseTypeCategory"
        {...exerciseReferenceInputProps}
      >
        <AutocompleteInput
          fullWidth
          optionText="name"
          optionValue="id"
          label="Movement Type Category"
          filterToQuery={(name: string) => ({ name })}
          validate={required()}
        />
      </ReferenceInput>
      <TextInput source="name" label="Name" validate={required()} fullWidth />
    </SimpleForm>
  </Create>
);

export const ExerciseBaseCreate: React.FC = () => (
  <Create resource="ExerciseBase" redirect="show">
    <SimpleForm>
      <ReferenceInput
        source="exerciseTypeId"
        reference="ExerciseType"
        {...exerciseReferenceInputProps}
      >
        <AutocompleteInput
          fullWidth
          optionText="name"
          optionValue="id"
          label="Movement Type"
          filterToQuery={(name: string) => ({ name })}
          validate={required()}
        />
      </ReferenceInput>
      <TextInput source="name" label="Name" validate={required()} fullWidth />
      <NumberInput source="progression" label="Progression" step={1} />
    </SimpleForm>
  </Create>
);

export const ExerciseTypeCategoryGroupEdit: React.FC = () => (
  <Edit resource="ExerciseTypeCategoryGroup" redirect={false}>
    <SimpleForm
      toolbar={
        <CategoryHierarchyEditToolbar resource="ExerciseTypeCategoryGroup" />
      }
    >
      <TextInput source="id" disabled />
      <TextInput source="name" label="Name" validate={required()} fullWidth />
    </SimpleForm>
  </Edit>
);

export const ExerciseTypeCategoryEdit: React.FC = () => (
  <Edit resource="ExerciseTypeCategory" redirect={false}>
    <SimpleForm
      toolbar={<CategoryHierarchyEditToolbar resource="ExerciseTypeCategory" />}
    >
      <TextInput source="id" disabled />
      <ReferenceInput
        source="exerciseTypeCategoryGroup.id"
        reference="ExerciseTypeCategoryGroup"
        {...exerciseReferenceInputProps}
      >
        <AutocompleteInput
          fullWidth
          optionText="name"
          optionValue="id"
          label="MTC Group"
          filterToQuery={(name: string) => ({ name })}
          validate={required()}
        />
      </ReferenceInput>
      <TextInput source="name" label="Name" validate={required()} fullWidth />
    </SimpleForm>
  </Edit>
);

export const ExerciseTypeEdit: React.FC = () => (
  <Edit resource="ExerciseType" redirect={false}>
    <SimpleForm
      toolbar={<CategoryHierarchyEditToolbar resource="ExerciseType" />}
    >
      <TextInput source="id" disabled />
      <ReferenceInput
        source="exerciseTypeCategory.id"
        reference="ExerciseTypeCategory"
        {...exerciseReferenceInputProps}
      >
        <AutocompleteInput
          fullWidth
          optionText="name"
          optionValue="id"
          label="Movement Type Category"
          filterToQuery={(name: string) => ({ name })}
          validate={required()}
        />
      </ReferenceInput>
      <TextInput source="name" label="Name" validate={required()} fullWidth />
    </SimpleForm>
  </Edit>
);

export const ExerciseBaseEdit: React.FC = () => (
  <Edit resource="ExerciseBase" redirect={false}>
    <SimpleForm
      toolbar={<CategoryHierarchyEditToolbar resource="ExerciseBase" />}
    >
      <TextInput source="id" disabled />
      <ReferenceInput
        source="exerciseType.id"
        reference="ExerciseType"
        {...exerciseReferenceInputProps}
      >
        <AutocompleteInput
          fullWidth
          optionText="name"
          optionValue="id"
          label="Movement Type"
          filterToQuery={(name: string) => ({ name })}
          validate={required()}
        />
      </ReferenceInput>
      <TextInput source="name" label="Name" validate={required()} fullWidth />
      <NumberInput source="progression" label="Progression" step={1} />
    </SimpleForm>
  </Edit>
);

const EXERCISE_TYPE_CATEGORY_TABLE_COLUMNS: ChildrenTableColumn[] = [
  { source: 'name', label: 'Name' },
  {
    source: 'exerciseTypeCategoryGroup.name',
    label: 'MTC Group',
  },
];

const EXERCISE_TYPE_TABLE_COLUMNS: ChildrenTableColumn[] = [
  { source: 'name', label: 'Name' },
  {
    source: 'exerciseTypeCategory.exerciseTypeCategoryGroup.name',
    label: 'MTC Group',
  },
];

const EXERCISE_BASE_TABLE_COLUMNS: ChildrenTableColumn[] = [
  { source: 'name', label: 'Name' },
  { source: 'progression', label: 'Progression' },
  {
    source: 'exerciseType.exerciseTypeCategory.name',
    label: 'MTC',
  },
  {
    source: 'exerciseType.exerciseTypeCategory.exerciseTypeCategoryGroup.name',
    label: 'MTC Group',
  },
];

export const ExerciseTypeCategoryGroupShow: React.FC = () => (
  <Show resource="ExerciseTypeCategoryGroup">
    <SimpleShowLayout>
      <ClipboardField source="id" fontSize={14} />
      <TextField source="name" label="Name" />
      <FunctionField
        label=""
        render={(record: AdminExerciseTypeCategoryGroupBasicsFragment) => (
          <ChildrenTableSection
            label="Movement Type Categories"
            count={record.exerciseTypeCategories?.count ?? 0}
            resource="ExerciseTypeCategory"
            filter={{ exerciseTypeCategoryGroupId: String(record.id) }}
            columns={EXERCISE_TYPE_CATEGORY_TABLE_COLUMNS}
          />
        )}
      />
    </SimpleShowLayout>
  </Show>
);

export const ExerciseTypeCategoryShow: React.FC = () => (
  <Show resource="ExerciseTypeCategory">
    <SimpleShowLayout>
      <ClipboardField source="id" fontSize={14} />
      <TextField source="name" label="Name" />
      <FunctionField
        label="MTC Group"
        render={(record: ExerciseTypeCategoryRecord) => {
          const group = record.exerciseTypeCategoryGroup;
          const groupId =
            group?.id ?? record.exerciseTypeCategoryGroupId ?? undefined;
          const groupName = group?.name;

          if (!groupName) {
            return '-';
          }

          if (!groupId) {
            return groupName;
          }

          return (
            <ResourceLink name="ExerciseTypeCategoryGroup" id={String(groupId)}>
              {String(groupName)}
            </ResourceLink>
          );
        }}
      />
      <FunctionField
        label=""
        render={(record: AdminExerciseTypeCategoryBasicsFragment) => (
          <ChildrenTableSection
            label="Movement Types"
            count={record.exerciseTypes?.count ?? 0}
            resource="ExerciseType"
            filter={{ exerciseTypeCategoryId: String(record.id) }}
            columns={EXERCISE_TYPE_TABLE_COLUMNS}
          />
        )}
      />
    </SimpleShowLayout>
  </Show>
);

const EXERCISE_TABLE_COLUMNS: ChildrenTableColumn[] = [
  { source: 'name', label: 'Name' },
  { source: 'displayName', label: 'Display Name' },
  { source: 'exerciseBase.name', label: 'Base Movement' },
  { source: 'exerciseBase.exerciseType.name', label: 'Movement Type' },
  {
    source: 'exerciseBase.exerciseType.exerciseTypeCategory.name',
    label: 'MTC',
  },
  {
    source:
      'exerciseBase.exerciseType.exerciseTypeCategory.exerciseTypeCategoryGroup.name',
    label: 'MTC Group',
  },
  { source: 'progression', label: 'Progression' },
];

export const ExerciseTypeShow: React.FC = () => (
  <Show resource="ExerciseType">
    <SimpleShowLayout>
      <ClipboardField source="id" fontSize={14} />
      <TextField source="name" label="Name" />
      <FunctionField
        label="MTC Group"
        render={(record: ExerciseTypeRecord) => {
          const { exerciseTypeCategory: category } = record;
          const group = category?.exerciseTypeCategoryGroup;
          const groupId = group?.id;
          const groupName = group?.name;

          if (!groupName) {
            return '-';
          }

          if (!groupId) {
            return groupName;
          }

          return (
            <ResourceLink name="ExerciseTypeCategoryGroup" id={String(groupId)}>
              {String(groupName)}
            </ResourceLink>
          );
        }}
      />
      <FunctionField
        label="MTC"
        render={(record: ExerciseTypeRecord) => {
          const category = record.exerciseTypeCategory;
          const categoryId =
            category?.id ?? record.exerciseTypeCategoryId ?? undefined;
          const categoryName = category?.name;

          if (!categoryName) {
            return '-';
          }

          if (!categoryId) {
            return String(categoryName);
          }

          return (
            <ResourceLink name="ExerciseTypeCategory" id={String(categoryId)}>
              {String(categoryName)}
            </ResourceLink>
          );
        }}
      />
      <FunctionField
        label=""
        render={(record: ExerciseTypeRecord) => (
          <ChildrenTableSection
            label="Base Movements"
            count={record.exerciseBases?.count ?? 0}
            resource="ExerciseBase"
            filter={{ exerciseTypeId: String(record.id) }}
            columns={EXERCISE_BASE_TABLE_COLUMNS}
          />
        )}
      />
    </SimpleShowLayout>
  </Show>
);

export const ExerciseBaseShow: React.FC = () => (
  <Show resource="ExerciseBase">
    <SimpleShowLayout>
      <ClipboardField source="id" fontSize={14} />
      <TextField source="name" label="Name" />
      <FunctionField
        label="MTC Group"
        render={(record: ExerciseBaseRecord) => {
          const exerciseType = record.exerciseType;
          const group =
            exerciseType?.exerciseTypeCategory?.exerciseTypeCategoryGroup;
          const groupId = group?.id;
          const groupName = group?.name;

          if (!groupName) {
            return '-';
          }

          if (!groupId) {
            return groupName;
          }

          return (
            <ResourceLink name="ExerciseTypeCategoryGroup" id={String(groupId)}>
              {String(groupName)}
            </ResourceLink>
          );
        }}
      />
      <FunctionField
        label="MTC"
        render={(record: ExerciseBaseRecord) => {
          const cat = record.exerciseType?.exerciseTypeCategory;
          const categoryId = cat?.id;
          const categoryName = cat?.name;

          if (!categoryName) {
            return '-';
          }

          if (!categoryId) {
            return String(categoryName);
          }

          return (
            <ResourceLink name="ExerciseTypeCategory" id={String(categoryId)}>
              {String(categoryName)}
            </ResourceLink>
          );
        }}
      />
      <FunctionField
        label="Movement Type"
        render={(record: ExerciseBaseRecord) => {
          const exerciseType = record.exerciseType;
          const exerciseTypeId =
            exerciseType?.id ?? record.exerciseTypeId ?? undefined;
          const exerciseTypeName = exerciseType?.name;

          if (!exerciseTypeName) {
            return '-';
          }

          if (!exerciseTypeId) {
            return String(exerciseTypeName);
          }

          return (
            <ResourceLink name="ExerciseType" id={String(exerciseTypeId)}>
              {String(exerciseTypeName)}
            </ResourceLink>
          );
        }}
      />
      <NumberField source="progression" label="Progression" />
      <FunctionField
        label=""
        render={(record: ExerciseBaseRecord) => (
          <ChildrenTableSection
            label="Movements"
            count={record.exercises?.count ?? 0}
            resource="Exercise"
            filter={{ exerciseBaseId: String(record.id) }}
            columns={EXERCISE_TABLE_COLUMNS}
            sort={{ field: 'progression', order: 'ASC' }}
          />
        )}
      />
    </SimpleShowLayout>
  </Show>
);
